import {Controller} from "@hotwired/stimulus"
import {Chart, LineController, LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend, Colors} from "chart.js"
import "chartjs-adapter-luxon"

Chart.register(LineController, LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend, Colors)

function deepMerge(base, override) {
    const result = {...base}
    for (const [key, value] of Object.entries(override)) {
        result[key] = value?.constructor === Object && base[key]?.constructor === Object ? deepMerge(base[key], value) : value
    }
    return result
}

// Container div holds the controller + values and MUST own its size (CSS aspect-ratio or
// explicit height) — Chart.js clamps resizes to the container height, so a container sized
// by the canvas ratchets and never grows back. The canvas target carries data-turbo-permanent
// + a unique id; creation rides the target callbacks because Turbo visits deliver the
// permanent canvas only after connect() (placeholder swap).
export class ChartController extends Controller {
    static targets = ["canvas"]
    static values = {
        data: Object,
        options: {type: Object, default: {}},
        type: {type: String, default: "line"},
        animation: {type: Boolean, default: true}
    }

    canvasTargetConnected(canvas) {
        Chart.getChart(canvas)?.destroy()
        this.chart = new Chart(canvas.getContext("2d"), {
            type: this.typeValue,
            data: this.dataValue,
            options: deepMerge(this.defaultOptions(), this.optionsValue)
        })
    }

    canvasTargetDisconnected() {
        this.teardown()
    }

    dataValueChanged() {
        if (!this.chart) return
        this.chart.data = this.dataValue
        this.chart.update("none")
    }

    disconnect() {
        this.teardown()
    }

    teardown() {
        // a transplanted canvas may already belong to a newer controller's chart
        if (this.chart && Chart.getChart(this.chart.canvas) === this.chart) this.chart.destroy()
        this.chart = undefined
    }

    defaultOptions() {
        return {
            animation: this.animationValue,
            interaction: {intersect: false, mode: "index"},
            responsive: true,
            maintainAspectRatio: false,
            parsing: false,
            scales: {
                x: {
                    type: "time",
                    time: {displayFormats: {hour: "HH:mm"}, unit: "hour"},
                    ticks: {major: {enabled: true}, font: context => context.tick?.major ? {weight: "bold"} : undefined}
                },
                y: {beginAtZero: true}
            }
        }
    }
}
